import React, { useEffect, useState } from "react";
import { listActions, approveAction, editAction, dismissAction } from "@/lib/api";
import { toast } from "sonner";
import { Check, Edit3, X, Save } from "lucide-react";

export default function Review() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState({});
    const [busy, setBusy] = useState({});

    const load = async () => {
        const rows = await listActions("needs_review");
        setItems(rows);
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
    }, []);

    const startEdit = (a) => {
        setEditing((e) => ({
            ...e,
            [a.id]: {
                subject: a.draft?.subject || "",
                body: a.draft?.body || "",
            },
        }));
    };

    const cancelEdit = (id) => {
        setEditing((e) => {
            const c = { ...e };
            delete c[id];
            return c;
        });
    };

    const run = async (id, fn, label) => {
        setBusy((b) => ({ ...b, [id]: true }));
        try {
            await fn();
            toast.success(`${label} · ${id.slice(0, 8)}`);
            cancelEdit(id);
            await load();
        } catch (e) {
            toast.error(`${label} failed`);
        } finally {
            setBusy((b) => ({ ...b, [id]: false }));
        }
    };

    return (
        <div data-testid="review-page">
            <div className="mb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#ff9f0a] mb-2">
                    // human in the loop
                </div>
                <h1 className="text-4xl font-bold tracking-tight">Manual Review Queue</h1>
                <p className="font-mono text-[11px] text-[#9ca3af] mt-1">
                    Approve, edit, or dismiss AI-generated actions before they ship.
                </p>
            </div>

            {items.length === 0 && (
                <div className="p-16 text-center border border-dashed border-[#262a2e]" data-testid="review-empty">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7cc93c]">
                        inbox zero
                    </div>
                    <div className="font-mono text-[11px] text-[#9ca3af] mt-2">
                        No drafts waiting on you.
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {items.map((a) => {
                    const isEditing = !!editing[a.id];
                    const draft = isEditing ? editing[a.id] : a.draft || { subject: "", body: "" };

                    return (
                        <div
                            key={a.id}
                            className="border border-[#262a2e] bg-[#111315] p-5 pc-fade-up"
                            data-testid={`review-card-${a.id}`}
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#9ca3af]">
                                    {a.workflow_name || a.type}
                                </div>
                                <div className="font-mono text-[9px] text-[#9ca3af]">
                                    {a.created_at?.slice(11, 19)}Z
                                </div>
                            </div>

                            <div className="font-mono text-[11px] text-[#9ca3af] mb-3">
                                → <span className="text-[#f3f4f6]">{a.target || "(no target)"}</span>
                            </div>

                            {isEditing ? (
                                <>
                                    <input
                                        value={draft.subject}
                                        onChange={(e) =>
                                            setEditing((x) => ({
                                                ...x,
                                                [a.id]: { ...draft, subject: e.target.value },
                                            }))
                                        }
                                        data-testid={`review-edit-subject-${a.id}`}
                                        className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 mb-2 focus:border-[#7cc93c] outline-none"
                                        placeholder="Subject"
                                    />
                                    <textarea
                                        value={draft.body}
                                        onChange={(e) =>
                                            setEditing((x) => ({
                                                ...x,
                                                [a.id]: { ...draft, body: e.target.value },
                                            }))
                                        }
                                        data-testid={`review-edit-body-${a.id}`}
                                        rows={8}
                                        className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none resize-none"
                                    />
                                </>
                            ) : (
                                <div className="bg-[#090a0b] border border-[#262a2e] p-3 font-mono text-[12px] text-[#f3f4f6] whitespace-pre-wrap max-h-60 overflow-auto">
                                    <div className="text-[#9ca3af] text-[10px] uppercase tracking-[0.2em] mb-2">
                                        subject
                                    </div>
                                    <div className="mb-3">{draft.subject}</div>
                                    <div className="text-[#9ca3af] text-[10px] uppercase tracking-[0.2em] mb-2">
                                        body
                                    </div>
                                    <div>{draft.body}</div>
                                </div>
                            )}

                            <div className="flex items-center gap-2 mt-4">
                                {isEditing ? (
                                    <>
                                        <CardBtn
                                            onClick={() =>
                                                run(
                                                    a.id,
                                                    () => editAction(a.id, draft),
                                                    "Saved & run"
                                                )
                                            }
                                            disabled={busy[a.id]}
                                            icon={Save}
                                            label="Save & run"
                                            accent="#7cc93c"
                                            testid={`review-save-${a.id}`}
                                        />
                                        <CardBtn
                                            onClick={() => cancelEdit(a.id)}
                                            icon={X}
                                            label="Cancel"
                                            accent="#9ca3af"
                                            testid={`review-cancel-${a.id}`}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <CardBtn
                                            onClick={() =>
                                                run(a.id, () => approveAction(a.id), "Approved & run")
                                            }
                                            disabled={busy[a.id]}
                                            icon={Check}
                                            label="Approve & run"
                                            accent="#7cc93c"
                                            testid={`review-approve-${a.id}`}
                                        />
                                        <CardBtn
                                            onClick={() => startEdit(a)}
                                            icon={Edit3}
                                            label="Edit"
                                            accent="#f3f4f6"
                                            testid={`review-edit-${a.id}`}
                                        />
                                        <CardBtn
                                            onClick={() =>
                                                run(a.id, () => dismissAction(a.id), "Dismissed")
                                            }
                                            disabled={busy[a.id]}
                                            icon={X}
                                            label="Dismiss"
                                            accent="#ff453a"
                                            testid={`review-dismiss-${a.id}`}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function CardBtn({ onClick, disabled, icon: Icon, label, accent, testid }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-[2px] border border-[#262a2e] text-[#9ca3af] disabled:opacity-40 transition-colors"
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = accent;
                e.currentTarget.style.color = accent;
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#262a2e";
                e.currentTarget.style.color = "#9ca3af";
            }}
        >
            <Icon className="w-3 h-3" />
            {label}
        </button>
    );
}
